/**
 * Submit-time validation for EV coverage manual edits.
 *
 * Effective date and plan end date are required and must be valid dates.
 * Cross-field checks run when the relevant dates can be parsed.
 * Plan end vs date of service: when status is **active**, plan end must be on or after DOS
 * (same calendar day is valid). When **inactive**, plan end must be strictly before DOS.
 *
 * @module coverageInfo/helpers/evCoverageEditValidation
 */

import moment from "moment";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import type { SingleCoverageInformation } from "../../../data-model/coverageInformation";
import {
  CoverageMiscellaneousAmountType,
  CoveragePlanDisplayStatus,
  CoverageSubscriberType,
} from "../../../enums/coverageInformationEnums";
import { EV_COVERAGE_CONTINUE_VALIDATION_DATE_OF_WORK_CUTOFF } from "../config/evCoverageEditFlags";

/** Stable ids for editable EV coverage fields (used with Oasis `LabeledValue` `required`). */
export const EvCoverageEditFieldId = {
  PLAN_NAME: "planName",
  MEMBER_ID: "memberId",
  PLAN_STATUS: "planStatus",
  EFFECTIVE_DATE: "effectiveDate",
  PLAN_END_DATE: "planEndDate",
  IND_DED_TOTAL: "indDedTotal",
  IND_DED_USED: "indDedUsed",
  FAM_DED_TOTAL: "famDedTotal",
  FAM_DED_USED: "famDedUsed",
  IND_OOP_TOTAL: "indOopTotal",
  IND_OOP_USED: "indOopUsed",
  FAM_OOP_TOTAL: "famOopTotal",
  FAM_OOP_USED: "famOopUsed",
  CO_PAY: "coPay",
  CO_INSURANCE: "coInsurance",
} as const;

/** Union of {@link EvCoverageEditFieldId} object values. */
export type EvCoverageFieldId =
  (typeof EvCoverageEditFieldId)[keyof typeof EvCoverageEditFieldId];

/** Options controlling which plan/financial fields are validated. */
export interface ValidateEvCoverageEditSnapshotOptions {
  includePlanNameAndMemberId: boolean;
  activeTreatmentTabIndex: number;
  requireBvFields: boolean;
  requireCopayCoinsurance: boolean;
}

/** Options for validating every insurance tier and treatment tab before Continue. */
export interface ValidateAllEvCoverageTabsOptions {
  includePlanNameAndMemberId: boolean;
  requireBvFields: boolean;
  requireCopayCoinsurance: boolean;
}

/** One coverage tab snapshot paired with its insurance-tier tab index for Continue validation. */
export interface EvCoverageContinueValidationEntry {
  snapshot: SingleCoverageInformation;
  coverageTabIndex: number;
}

/** One failed insurance/treatment tab pair from {@link validateAllEvCoverageTabsForContinue}. */
export interface EvCoverageTabValidationFailure {
  coverageTabIndex: number;
  treatmentTabIndex: number;
  result: EvCoverageEditValidationResult;
}

/** Combined outcome when validating all coverage tabs before Continue. */
export interface ValidateAllEvCoverageTabsResult {
  valid: boolean;
  failures: EvCoverageTabValidationFailure[];
  /** First invalid tab pair in insurance-then-treatment order. */
  firstFailure?: EvCoverageTabValidationFailure;
  issues: Set<EvCoverageValidationIssue>;
}

/** Result of {@link validateEvCoverageEditSnapshot}. */
export interface EvCoverageEditValidationResult {
  valid: boolean;
  invalidFieldIds: Set<EvCoverageFieldId>;
  issues: Set<EvCoverageValidationIssue>;
}

/**
 * Non-empty validation issue categories surfaced as save-time toasts.
 */
export enum EvCoverageValidationIssue {
  PLAN_END_NOT_AFTER_EFFECTIVE = "planEndNotAfterEffective",
  /** Active plan: plan end is not on or after the order date of service. */
  PLAN_END_NOT_AFTER_DOS = "planEndNotAfterDos",
  /** Inactive plan: plan end is not strictly before the order date of service. */
  PLAN_END_NOT_BEFORE_DOS = "planEndNotBeforeDos",
  USED_EXCEEDS_TOTAL = "usedExceedsTotal",
  CO_INSURANCE_EXCEEDS_MAX = "coInsuranceExceedsMax",
}

/**
 * Returns true when a text field is empty or a display placeholder.
 *
 * @param value - Raw string from plan summary inputs
 * @returns Whether the value must be filled before save
 */
export const isEmptyEvCoverageText = (value: string | undefined): boolean => {
  const trimmed = (value ?? "").trim();
  if (trimmed === "" || trimmed === "—") {
    return true;
  }
  const upper = trimmed.toUpperCase();
  return upper === "NA" || upper === "N/A";
};

/**
 * Returns true when a plan date is missing or not parseable.
 *
 * @param iso - ISO or calendar date string from the model
 * @returns Whether the date must be set before save
 */
export const isEmptyEvCoverageDate = (iso: string | undefined): boolean => {
  if (!iso || !iso.trim()) {
    return true;
  }
  return !moment(iso, ["YYYY-MM-DD", moment.ISO_8601], true).isValid();
};

/**
 * Parses an EV coverage date string into a strict Moment instance.
 *
 * @param iso - Date string entered in plan fields
 * @returns Strict moment instance, or undefined when invalid
 */
const parseEvCoverageDate = (
  iso: string | undefined,
): moment.Moment | undefined => {
  if (!iso || !iso.trim()) {
    return undefined;
  }
  const parsed = moment(iso, ["YYYY-MM-DD", moment.ISO_8601], true);
  return parsed.isValid() ? parsed : undefined;
};

/**
 * Returns true when a raw BV amount is the `NA` literal.
 *
 * @param raw - User- or payer-supplied text
 * @returns Whether the value is `NA` / `N/A`
 */
const isNaFinancialLiteral = (raw: string | undefined): boolean => {
  const upper = (raw ?? "").trim().toUpperCase();
  return upper === "NA" || upper === "N/A";
};

/**
 * Returns true when a BV amount has a numeric value or `NA` (zero is allowed).
 *
 * @param raw - Raw text from deductible/OOP/misc fields
 * @param parsed - Parsed number when available
 * @returns Whether the field satisfies required BV input rules
 */
export const isFilledEvCoverageBvAmount = (
  raw: string | undefined,
  parsed: number | undefined,
): boolean => {
  if (parsed !== undefined && !Number.isNaN(parsed)) {
    return true;
  }
  if (isNaFinancialLiteral(raw)) {
    return true;
  }
  const trimmed = (raw ?? "").trim();
  if (trimmed === "" || trimmed === "-" || trimmed === "—") {
    return false;
  }
  const numericPart = trimmed.replace(/[$%,]/g, "").replace(/,/g, "").trim();
  const n = Number.parseFloat(numericPart);
  return Number.isFinite(n);
};

/**
 * Returns true when a BV amount is missing (not numeric and not `NA`).
 *
 * @param raw - Raw text from deductible/OOP/misc fields
 * @param parsed - Parsed number when available
 * @returns Whether the amount must be set before save
 */
export const isEmptyEvCoverageBvAmount = (
  raw: string | undefined,
  parsed: number | undefined,
): boolean => !isFilledEvCoverageBvAmount(raw, parsed);

/**
 * Returns true when copay / co-insurance is empty (numeric or `NA` are valid).
 *
 * @param amount - Stored miscellaneous amount string
 * @returns Whether the misc amount must be set before save
 */
export const isEmptyEvCoverageMiscAmount = (
  amount: string | undefined,
): boolean => isEmptyEvCoverageBvAmount(amount, undefined);

/**
 * Parses co-insurance text input into a percentage number.
 *
 * @param amount - User-entered co-insurance value (e.g. `20%`, `20`)
 * @returns Parsed percent value, or undefined when input is not numeric
 */
const parseCoInsurancePercent = (
  amount: string | undefined,
): number | undefined => {
  const trimmed = (amount ?? "").trim();
  if (!trimmed || trimmed === "—" || trimmed === "-") {
    return undefined;
  }
  const upper = trimmed.toUpperCase();
  if (upper === "NA" || upper === "N/A") {
    return undefined;
  }
  const numeric = trimmed.replace(/%/g, "").replace(/,/g, "").trim();
  const parsed = Number.parseFloat(numeric);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Flags both fields when the used amount exceeds total (equality is allowed).
 *
 * @param total - Total deductible/OOP amount
 * @param used - Used deductible/OOP amount
 * @param totalFieldId - Field id for total amount
 * @param usedFieldId - Field id for used amount
 * @param invalidFieldIds - Mutable invalid field-id set
 */
const validateUsedDoesNotExceedTotal = (
  totalRaw: string | undefined,
  total: number | undefined,
  usedRaw: string | undefined,
  used: number | undefined,
  totalFieldId: EvCoverageFieldId,
  usedFieldId: EvCoverageFieldId,
  invalidFieldIds: Set<EvCoverageFieldId>,
): void => {
  if (
    isNaFinancialLiteral(totalRaw) ||
    isNaFinancialLiteral(usedRaw) ||
    isEmptyEvCoverageBvAmount(totalRaw, total) ||
    isEmptyEvCoverageBvAmount(usedRaw, used) ||
    total === undefined ||
    used === undefined
  ) {
    return;
  }
  if (used > total) {
    invalidFieldIds.add(totalFieldId);
    invalidFieldIds.add(usedFieldId);
  }
};

/**
 * Returns true when used amount exceeds total for comparable numeric pairs.
 *
 * @param totalRaw - Raw total text
 * @param total - Parsed total
 * @param usedRaw - Raw used text
 * @param used - Parsed used
 * @returns Whether the pair violates the used ≤ total rule
 */
const bvUsedExceedsTotal = (
  totalRaw: string | undefined,
  total: number | undefined,
  usedRaw: string | undefined,
  used: number | undefined,
): boolean => {
  if (
    isNaFinancialLiteral(totalRaw) ||
    isNaFinancialLiteral(usedRaw) ||
    total === undefined ||
    used === undefined
  ) {
    return false;
  }
  return used > total;
};

/**
 * Resolves the patient-responsibility slice for the active treatment tab.
 *
 * @param snapshot - Coverage working copy to validate
 * @param tabIndex - Selected treatment tab index
 * @returns PR row for the tab, if any
 */
const getActivePatientResponsibility = (
  snapshot: SingleCoverageInformation,
  tabIndex: number,
) => {
  const entries = snapshot.patientResponsibilityInformation ?? [];
  if (entries.length === 0) {
    return undefined;
  }
  const safeIndex = Math.min(Math.max(0, tabIndex), entries.length - 1);
  return entries[safeIndex]?.singlePatientResponsibility;
};

/**
 * Validates an EV coverage edit snapshot before Postgres persist.
 * Patient-responsibility (BV) fields are skipped when plan status is inactive.
 * Effective date and plan end date are required. The plan-end-vs-effective-date
 * and plan-end-vs-date-of-service checks run when their dates can be parsed.
 *
 * Medicaid tiers (`snapshot.medicaidInformation`) skip the active-treatment BV /
 * copay / coinsurance required-field and used-vs-total checks — those financial
 * editors are not rendered for Medicaid.
 *
 * @param snapshot - Working copy at save time
 * @param options - Which plan fields and treatment tab to include
 * @returns Validation outcome and ids for Oasis `required` styling (required + cross-field invariants)
 */
export const validateEvCoverageEditSnapshot = (
  snapshot: SingleCoverageInformation,
  options: ValidateEvCoverageEditSnapshotOptions,
): EvCoverageEditValidationResult => {
  const invalidFieldIds = new Set<EvCoverageFieldId>();
  const issues = new Set<EvCoverageValidationIssue>();
  const { planInformation } = snapshot;
  const status = planInformation.planStatus;

  if (options.includePlanNameAndMemberId) {
    if (isEmptyEvCoverageText(planInformation.planName)) {
      invalidFieldIds.add(EvCoverageEditFieldId.PLAN_NAME);
    }
    if (isEmptyEvCoverageText(planInformation.memberId)) {
      invalidFieldIds.add(EvCoverageEditFieldId.MEMBER_ID);
    }
    if (
      status !== CoveragePlanDisplayStatus.ACTIVE &&
      status !== CoveragePlanDisplayStatus.INACTIVE
    ) {
      invalidFieldIds.add(EvCoverageEditFieldId.PLAN_STATUS);
    }
  }

  if (isEmptyEvCoverageDate(planInformation.effectiveDate)) {
    invalidFieldIds.add(EvCoverageEditFieldId.EFFECTIVE_DATE);
  }
  if (isEmptyEvCoverageDate(planInformation.planEndDate)) {
    invalidFieldIds.add(EvCoverageEditFieldId.PLAN_END_DATE);
  }
  // Check date ordering only when the relevant dates are valid.
  const effectiveDate = parseEvCoverageDate(planInformation.effectiveDate);
  const planEndDate = parseEvCoverageDate(planInformation.planEndDate);
  const dateOfService = parseEvCoverageDate(
    snapshot.orderInformation.dateOfService,
  );
  if (effectiveDate && planEndDate && !planEndDate.isAfter(effectiveDate)) {
    invalidFieldIds.add(EvCoverageEditFieldId.EFFECTIVE_DATE);
    invalidFieldIds.add(EvCoverageEditFieldId.PLAN_END_DATE);
    issues.add(EvCoverageValidationIssue.PLAN_END_NOT_AFTER_EFFECTIVE);
  }
  // Active: plan end must be on or after DOS (same calendar day is valid).
  if (
    dateOfService &&
    planEndDate &&
    status === CoveragePlanDisplayStatus.ACTIVE &&
    !planEndDate.isSameOrAfter(dateOfService, "day")
  ) {
    invalidFieldIds.add(EvCoverageEditFieldId.PLAN_END_DATE);
    issues.add(EvCoverageValidationIssue.PLAN_END_NOT_AFTER_DOS);
  }
  // Inactive: plan end must be strictly before DOS (same calendar day is invalid).
  if (
    dateOfService &&
    planEndDate &&
    status === CoveragePlanDisplayStatus.INACTIVE &&
    !planEndDate.isBefore(dateOfService, "day")
  ) {
    invalidFieldIds.add(EvCoverageEditFieldId.PLAN_END_DATE);
    issues.add(EvCoverageValidationIssue.PLAN_END_NOT_BEFORE_DOS);
  }

  const activePr = getActivePatientResponsibility(
    snapshot,
    options.activeTreatmentTabIndex,
  );

  // Medicaid tiers render a read-only Treatment / Additional Payer view instead of
  // EvCoverageFinancialContent, so BV/copay/coinsurance required-field and
  // used-vs-total checks must not apply — those fields are never editable.
  if (
    !snapshot.medicaidInformation &&
    activePr &&
    status !== CoveragePlanDisplayStatus.INACTIVE
  ) {
    const individualDed = activePr.deductibleAmountInformation.find(
      (d) => d.subscriberType === CoverageSubscriberType.INDIVIDUAL,
    );
    const familyDed = activePr.deductibleAmountInformation.find(
      (d) => d.subscriberType === CoverageSubscriberType.FAMILY,
    );
    const individualOop = activePr.outOfPocketAmountInformation.find(
      (o) => o.subscriberType === CoverageSubscriberType.INDIVIDUAL,
    );
    const familyOop = activePr.outOfPocketAmountInformation.find(
      (o) => o.subscriberType === CoverageSubscriberType.FAMILY,
    );
    const coPay = activePr.miscellaneousAmountInformation.find(
      (m) => m.type === CoverageMiscellaneousAmountType.CO_PAY,
    )?.amount;
    const coInsurance = activePr.miscellaneousAmountInformation.find(
      (m) => m.type === CoverageMiscellaneousAmountType.CO_INSURANCE,
    )?.amount;

    if (options.requireBvFields) {
      if (
        isEmptyEvCoverageBvAmount(
          individualDed?.totalAmountRaw,
          individualDed?.totalAmount,
        )
      ) {
        invalidFieldIds.add(EvCoverageEditFieldId.IND_DED_TOTAL);
      }
      if (
        isEmptyEvCoverageBvAmount(
          individualDed?.usedAmountRaw,
          individualDed?.usedAmount,
        )
      ) {
        invalidFieldIds.add(EvCoverageEditFieldId.IND_DED_USED);
      }
      if (
        isEmptyEvCoverageBvAmount(
          individualOop?.totalAmountRaw,
          individualOop?.totalAmount,
        )
      ) {
        invalidFieldIds.add(EvCoverageEditFieldId.IND_OOP_TOTAL);
      }
      if (
        isEmptyEvCoverageBvAmount(
          individualOop?.usedAmountRaw,
          individualOop?.usedAmount,
        )
      ) {
        invalidFieldIds.add(EvCoverageEditFieldId.IND_OOP_USED);
      }
    }

    if (options.requireCopayCoinsurance) {
      if (isEmptyEvCoverageMiscAmount(coPay)) {
        invalidFieldIds.add(EvCoverageEditFieldId.CO_PAY);
      }
      if (isEmptyEvCoverageMiscAmount(coInsurance)) {
        invalidFieldIds.add(EvCoverageEditFieldId.CO_INSURANCE);
      }
    }

    validateUsedDoesNotExceedTotal(
      individualDed?.totalAmountRaw,
      individualDed?.totalAmount,
      individualDed?.usedAmountRaw,
      individualDed?.usedAmount,
      EvCoverageEditFieldId.IND_DED_TOTAL,
      EvCoverageEditFieldId.IND_DED_USED,
      invalidFieldIds,
    );
    validateUsedDoesNotExceedTotal(
      familyDed?.totalAmountRaw,
      familyDed?.totalAmount,
      familyDed?.usedAmountRaw,
      familyDed?.usedAmount,
      EvCoverageEditFieldId.FAM_DED_TOTAL,
      EvCoverageEditFieldId.FAM_DED_USED,
      invalidFieldIds,
    );
    validateUsedDoesNotExceedTotal(
      individualOop?.totalAmountRaw,
      individualOop?.totalAmount,
      individualOop?.usedAmountRaw,
      individualOop?.usedAmount,
      EvCoverageEditFieldId.IND_OOP_TOTAL,
      EvCoverageEditFieldId.IND_OOP_USED,
      invalidFieldIds,
    );
    validateUsedDoesNotExceedTotal(
      familyOop?.totalAmountRaw,
      familyOop?.totalAmount,
      familyOop?.usedAmountRaw,
      familyOop?.usedAmount,
      EvCoverageEditFieldId.FAM_OOP_TOTAL,
      EvCoverageEditFieldId.FAM_OOP_USED,
      invalidFieldIds,
    );
    if (
      bvUsedExceedsTotal(
        individualDed?.totalAmountRaw,
        individualDed?.totalAmount,
        individualDed?.usedAmountRaw,
        individualDed?.usedAmount,
      ) ||
      bvUsedExceedsTotal(
        familyDed?.totalAmountRaw,
        familyDed?.totalAmount,
        familyDed?.usedAmountRaw,
        familyDed?.usedAmount,
      ) ||
      bvUsedExceedsTotal(
        individualOop?.totalAmountRaw,
        individualOop?.totalAmount,
        individualOop?.usedAmountRaw,
        individualOop?.usedAmount,
      ) ||
      bvUsedExceedsTotal(
        familyOop?.totalAmountRaw,
        familyOop?.totalAmount,
        familyOop?.usedAmountRaw,
        familyOop?.usedAmount,
      )
    ) {
      issues.add(EvCoverageValidationIssue.USED_EXCEEDS_TOTAL);
    }

    const coInsurancePercent = parseCoInsurancePercent(coInsurance);
    if (coInsurancePercent !== undefined && coInsurancePercent > 100) {
      invalidFieldIds.add(EvCoverageEditFieldId.CO_INSURANCE);
      issues.add(EvCoverageValidationIssue.CO_INSURANCE_EXCEEDS_MAX);
    }
  }

  return {
    valid: invalidFieldIds.size === 0,
    invalidFieldIds,
    issues,
  };
};

/**
 * Returns treatment-tab indices to validate for a coverage snapshot.
 *
 * @param snapshot - Coverage row to inspect
 * @returns Tab indices (at least `[0]` when no patient-responsibility rows exist)
 */
const getTreatmentTabIndicesForValidation = (
  snapshot: SingleCoverageInformation,
): number[] => {
  const prCount = snapshot.patientResponsibilityInformation?.length ?? 0;
  if (prCount === 0) {
    return [0];
  }
  return Array.from({ length: prCount }, (_, index) => index);
};

/**
 * Validates selected insurance tiers and their treatment tabs before the coverage Continue action.
 * Uses the same rules as Save via {@link validateEvCoverageEditSnapshot}.
 *
 * @param entries - Tab snapshots with stable insurance-tier indices (active tab may include unsaved edits)
 * @param options - Plan and BV required-field options
 * @returns Combined validation outcome with the first failing tab for focus/navigation
 */
export const validateAllEvCoverageTabsForContinue = (
  entries: EvCoverageContinueValidationEntry[],
  options: ValidateAllEvCoverageTabsOptions,
): ValidateAllEvCoverageTabsResult => {
  const failures: EvCoverageTabValidationFailure[] = [];
  const issues = new Set<EvCoverageValidationIssue>();

  entries.forEach(({ snapshot, coverageTabIndex }) => {
    getTreatmentTabIndicesForValidation(snapshot).forEach(
      (treatmentTabIndex) => {
        const result = validateEvCoverageEditSnapshot(snapshot, {
          includePlanNameAndMemberId: options.includePlanNameAndMemberId,
          activeTreatmentTabIndex: treatmentTabIndex,
          requireBvFields: options.requireBvFields,
          requireCopayCoinsurance: options.requireCopayCoinsurance,
        });
        if (!result.valid) {
          failures.push({ coverageTabIndex, treatmentTabIndex, result });
          result.issues.forEach((issue) => issues.add(issue));
        }
      },
    );
  });

  return {
    valid: failures.length === 0,
    failures,
    firstFailure: failures[0],
    issues,
  };
};

/**
 * Whether a field should pass Oasis `LabeledValue` `required` after a failed save.
 *
 * @param showFieldErrors - Parent flag: feature on and user has clicked Save
 * @param invalidFieldIds - Current invalid field ids from validation
 * @param fieldId - Field to check
 * @returns Value for `LabeledValue` `required` prop
 */
export const evCoverageFieldShowsRequired = (
  showFieldErrors: boolean,
  invalidFieldIds: ReadonlySet<EvCoverageFieldId>,
  fieldId: EvCoverageFieldId,
): boolean => showFieldErrors && invalidFieldIds.has(fieldId);

/**
 * Resolves the toast message for a failed EV coverage save validation.
 *
 * @param issues - Validation issue categories from {@link validateEvCoverageEditSnapshot}
 * @returns User-facing toast copy
 */
export const getEvCoverageSaveValidationToastMessage = (
  issues: Set<EvCoverageValidationIssue>,
): string => {
  if (issues.has(EvCoverageValidationIssue.USED_EXCEEDS_TOTAL)) {
    return ToastMessages.COVERAGE_SAVE_VALIDATION_USED_EXCEEDS_TOTAL;
  }
  if (issues.has(EvCoverageValidationIssue.PLAN_END_NOT_AFTER_EFFECTIVE)) {
    return ToastMessages.COVERAGE_SAVE_VALIDATION_PLAN_END_DATE;
  }
  if (issues.has(EvCoverageValidationIssue.PLAN_END_NOT_AFTER_DOS)) {
    return ToastMessages.COVERAGE_SAVE_VALIDATION_PLAN_END_AFTER_DOS;
  }
  if (issues.has(EvCoverageValidationIssue.PLAN_END_NOT_BEFORE_DOS)) {
    return ToastMessages.COVERAGE_SAVE_VALIDATION_PLAN_END_BEFORE_DOS;
  }
  if (issues.has(EvCoverageValidationIssue.CO_INSURANCE_EXCEEDS_MAX)) {
    return ToastMessages.COVERAGE_SAVE_VALIDATION_CO_INSURANCE_MAX;
  }
  return ToastMessages.COVERAGE_SAVE_VALIDATION_FAILURE;
};

/**
 * Resolves the toast message for a failed coverage Continue validation.
 *
 * @param issues - Validation issue categories from {@link validateAllEvCoverageTabsForContinue}
 * @returns User-facing toast copy
 */
export const getEvCoverageContinueValidationToastMessage = (
  issues: Set<EvCoverageValidationIssue>,
): string => {
  if (issues.size > 0) {
    return getEvCoverageSaveValidationToastMessage(issues);
  }
  return ToastMessages.COVERAGE_CONTINUE_VALIDATION_FAILURE;
};

/** Issue categories that show a specific toast instead of the generic required-fields message. */
const CONTINUE_VALIDATION_STRUCTURAL_ISSUES: ReadonlySet<EvCoverageValidationIssue> =
  new Set([
    EvCoverageValidationIssue.USED_EXCEEDS_TOTAL,
    EvCoverageValidationIssue.PLAN_END_NOT_AFTER_EFFECTIVE,
    EvCoverageValidationIssue.PLAN_END_NOT_AFTER_DOS,
    EvCoverageValidationIssue.PLAN_END_NOT_BEFORE_DOS,
    EvCoverageValidationIssue.CO_INSURANCE_EXCEEDS_MAX,
  ]);

/**
 * Whether Continue should run EV field validation for an order based on date of work.
 * Orders on or before {@link EV_COVERAGE_CONTINUE_VALIDATION_DATE_OF_WORK_CUTOFF}
 * skip validation; later orders run validation when the facility flag is enabled.
 * Missing or invalid dates run validation (user must set date of work; cutoff applies once set).
 *
 * @param dateOfWork - Order date of work ISO string from `timestamps.date_of_work`
 * @returns `true` when Continue validation should run
 */
export const shouldRunEvCoverageContinueValidationForDateOfWork = (
  dateOfWork: string | undefined | null,
): boolean => {
  if (!dateOfWork?.trim()) {
    return true;
  }
  const parsed = moment(dateOfWork, ["YYYY-MM-DD", moment.ISO_8601], true);
  if (!parsed.isValid()) {
    return true;
  }
  const cutoff = moment(
    EV_COVERAGE_CONTINUE_VALIDATION_DATE_OF_WORK_CUTOFF,
    "YYYY-MM-DD",
    true,
  );
  return parsed.isAfter(cutoff, "day");
};

/**
 * Whether a failed Continue validation should open local edit mode automatically.
 * Applies when the failure is missing required fields (generic toast), not cross-field
 * rules such as used-exceeds-total or plan-date constraints.
 *
 * @param issues - Validation issue categories from {@link validateAllEvCoverageTabsForContinue}
 * @returns True when the UI should enter edit mode after showing the required-fields toast
 */
export const shouldAutoEnterEditModeOnContinueValidationFailure = (
  issues: Set<EvCoverageValidationIssue>,
): boolean => {
  for (const issue of issues) {
    if (CONTINUE_VALIDATION_STRUCTURAL_ISSUES.has(issue)) {
      return false;
    }
  }
  return true;
};
